// Horizontal scroll-snap carousel with edge arrows.
// Markup contract:
//   <a3-carousel>
//     <button class="a3-carousel__btn a3-carousel__btn--prev">…</button>
//     <div class="a3-carousel__track"> …slides… </div>
//     <button class="a3-carousel__btn a3-carousel__btn--next">…</button>
//   </a3-carousel>
// Optional: data-marquee — slow continuous loop, paused on hover/focus/touch.
// Slides are cloned once for a seamless wrap. data-marquee="mobile" limits
// the loop to viewports under 990px (desktop renders a static row via CSS).
class A3Carousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.a3-carousel__track');
    if (!this.track) return;

    // The element can be detached and re-attached by host scripts; wire
    // listeners once, but restart the marquee loop on every reconnect.
    if (!this._wired) {
      this._wired = true;
      this.prevBtn = this.querySelector('.a3-carousel__btn--prev');
      this.nextBtn = this.querySelector('.a3-carousel__btn--next');
      this.prevBtn?.addEventListener('click', () => this.scrollByPage(-1));
      this.nextBtn?.addEventListener('click', () => this.scrollByPage(1));
      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
      window.addEventListener('resize', () => this.updateButtons(), { passive: true });
    }
    this.updateButtons();

    if (this.hasAttribute('data-marquee') && !this.marqueeSuppressed()) {
      if (this._marquee) this.startMarqueeLoop();
      else this.initMarquee();
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._raf);
  }

  // data-marquee="mobile": the section renders as a static row on desktop
  // (CSS), so the loop (and its slide clones) only run below 990px.
  marqueeSuppressed() {
    return this.getAttribute('data-marquee') === 'mobile' && window.matchMedia('(min-width: 990px)').matches;
  }

  initMarquee() {
    const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (noMotion || this._marquee) return;
    const slides = Array.from(this.track.children);
    if (slides.length < 2) return;
    this._marquee = true;

    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
      this.track.appendChild(clone);
    });
    this.classList.add('a3-carousel--marquee');
    this._loopStart = slides[0];
    this._loopEnd = this.track.children[slides.length]; // first clone

    this._paused = false;
    this.addEventListener('mouseenter', () => (this._paused = true));
    this.addEventListener('mouseleave', () => (this._paused = false));
    // Keyboard focus inside the carousel pauses it too
    this.addEventListener('focusin', () => (this._paused = true));
    this.addEventListener('focusout', () => (this._paused = false));
    // Touch: pause while the finger is down and briefly after, so swipes and
    // momentum scrolls aren't fought by the drift (paused mode absorbs them)
    this.track.addEventListener('touchstart', () => {
      clearTimeout(this._touchResume);
      this._paused = true;
    }, { passive: true });
    this.track.addEventListener('touchend', () => {
      clearTimeout(this._touchResume);
      this._touchResume = setTimeout(() => (this._paused = false), 1500);
    }, { passive: true });

    this.startMarqueeLoop();
  }

  startMarqueeLoop() {
    cancelAnimationFrame(this._raf);
    const SPEED = 0.03; // px per ms
    let pos = this.track.scrollLeft;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(now - last, 100);
      last = now;
      if (this._paused) {
        pos = this.track.scrollLeft; // absorb manual scrolls while paused
      } else {
        const loop = this._loopEnd.offsetLeft - this._loopStart.offsetLeft;
        pos += dt * SPEED;
        if (loop > 0 && pos >= loop) pos -= loop;
        this.track.scrollLeft = pos;
      }
      this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }

  scrollByPage(direction) {
    const page = this.track.clientWidth * 0.85;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    // Arrows wrap instead of dead-ending (client feedback round 1)
    if (direction > 0 && this.track.scrollLeft >= maxScroll - 4) {
      this.track.scrollTo({ left: 0, behavior });
    } else if (direction < 0 && this.track.scrollLeft <= 4) {
      this.track.scrollTo({ left: maxScroll, behavior });
    } else {
      this.track.scrollBy({ left: page * direction, behavior });
    }
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    // Arrows always active: they page through and wrap at the ends
    this.prevBtn.removeAttribute('disabled');
    this.nextBtn.removeAttribute('disabled');
  }
}
customElements.define('a3-carousel', A3Carousel);
